import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, useToast } from '../components/ui';
import { api } from '../lib/api';
import type { AuditPage } from '../lib/types';

/** Admins only: who did what, across every booklet, user and template. */
const ACTION_LABEL: Record<string, string> = {
  'sign-in': 'Signed in',
  'sign-in-failed': 'Failed sign-in',
  'sign-out': 'Signed out',
  password: 'Password',
  create: 'Created',
  edit: 'Edited',
  delete: 'Deleted',
  duplicate: 'Copied',
  status: 'Status',
  publish: 'Published',
  restore: 'Restored',
  import: 'Import',
  render: 'PDF',
  share: 'Shared',
  'share-stop': 'Stopped sharing',
  comment: 'Comment',
  'comment-resolve': 'Comment resolved',
  'comment-delete': 'Comment deleted',
  'photo-add': 'Photo added',
  'photo-delete': 'Photo deleted',
  'user-add': 'User added',
  'user-edit': 'User changed',
  'user-delete': 'User deleted',
  'template-add': 'Template added',
  'template-edit': 'Template changed',
  'template-delete': 'Template deleted',
};

/** Red for anything destructive, amber for sign-in trouble, green for publishing. */
const TONE: Record<string, string> = {
  delete: 'bad', 'user-delete': 'bad', 'template-delete': 'bad', 'photo-delete': 'bad', 'comment-delete': 'bad',
  'sign-in-failed': 'warn', restore: 'warn', 'share-stop': 'warn',
  publish: 'good', share: 'good', create: 'good', 'user-add': 'good', 'template-add': 'good',
};

const when = (iso: string) => new Date(iso).toLocaleString();

/** ::1 and ::ffff:10.0.0.1 mean nothing to a reader. */
const where = (ip: string | undefined) => {
  if (!ip) return '';
  if (ip === '::1' || ip === '127.0.0.1') return 'this server';
  return ip.replace('::ffff:', '');
};

export function ActivityPage() {
  const toast = useToast();
  const [page, setPage] = useState<AuditPage | null>(null);
  const [user, setUser] = useState('');
  const [action, setAction] = useState('');
  const [busy, setBusy] = useState(false);

  const load = (filter: { user?: string; action?: string }) => {
    setBusy(true);
    api.audit({ user: filter.user || undefined, action: filter.action || undefined })
      .then(setPage)
      .catch((e) => toast.push((e as Error).message, true))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load({ user, action }); }, [user, action]);

  if (!page) return <div className="row"><Spinner /> <span className="sub">Loading…</span></div>;

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="title">Activity</h1>
          <div className="sub">Everything the team has changed — booklets, photos, users and templates.</div>
        </div>
        <div style={{ flex: 1 }} />
        {busy && <Spinner />}
        <select className="inline-select" value={user} onChange={(e) => setUser(e.target.value)}>
          <option value="">Everyone</option>
          {page.users.map((u) => <option key={u} value={u}>@{u}</option>)}
        </select>
        <select className="inline-select" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Everything</option>
          {page.actions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] ?? a}</option>)}
        </select>
        <button className="btn small ghost" onClick={() => load({ user, action })}>Refresh</button>
      </div>

      {!page.entries.length ? (
        <div className="panel"><div className="sub">Nothing recorded yet.</div></div>
      ) : (
        <div className="panel">
          <table className="table plain activity">
            <thead>
              <tr><th>When</th><th>Who</th><th>What</th><th>Where</th></tr>
            </thead>
            <tbody>
              {page.entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="nowrap sub">{when(entry.at)}</td>
                  <td className="nowrap">@{entry.username}</td>
                  <td>
                    <span className={`act ${TONE[entry.action] ?? ''}`}>{ACTION_LABEL[entry.action] ?? entry.action}</span>
                    {' '}
                    {entry.target}
                    {entry.detail && <span className="sub"> · {entry.detail}</span>}
                  </td>
                  <td className="nowrap sub">
                    {entry.projectId && entry.action !== 'delete'
                      ? <Link to={`/projects/${entry.projectId}/preview`}>open booklet</Link>
                      : where(entry.ip)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sub" style={{ marginTop: 10 }}>
            Showing the {page.entries.length} most recent. Repeated edits to the same booklet within two minutes count as one line.
          </div>
        </div>
      )}
    </>
  );
}
